// CustomDashboard.jsx
import React, { useState } from 'react';
import { Plus, Trash2, Edit, BarChart3, LineChart, PieChart, TrendingUp, X, Save } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart as RechartsLine,
  Line,
  PieChart as RechartsPie,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

const CustomDashboard = () => {
  const [selectedForm, setSelectedForm] = useState('');
  const [showChartModal, setShowChartModal] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [dashboards, setDashboards] = useState([]);

  // Available forms with sample data
  const forms = [
    { 
      id: 1, 
      name: 'Test Form Alpha', 
      fields: ['Full Name', 'Email', 'District', 'Age', 'Status'],
      sampleData: [
        { 'Full Name': 'John Doe', Email: 'john@test.com', District: 'Chennai', Age: 25, Status: 'Active' },
        { 'Full Name': 'Jane Smith', Email: 'jane@test.com', District: 'Coimbatore', Age: 30, Status: 'Active' },
        { 'Full Name': 'Bob Johnson', Email: 'bob@test.com', District: 'Chennai', Age: 35, Status: 'Inactive' },
        { 'Full Name': 'Alice Brown', Email: 'alice@test.com', District: 'Madurai', Age: 28, Status: 'Active' },
        { 'Full Name': 'Charlie Wilson', Email: 'charlie@test.com', District: 'Coimbatore', Age: 32, Status: 'Active' },
        { 'Full Name': 'David Lee', Email: 'david@test.com', District: 'Chennai', Age: 27, Status: 'Active' },
        { 'Full Name': 'Emma Davis', Email: 'emma@test.com', District: 'Madurai', Age: 29, Status: 'Inactive' },
        { 'Full Name': 'Frank Miller', Email: 'frank@test.com', District: 'Salem', Age: 31, Status: 'Active' }
      ]
    },
    { 
      id: 2, 
      name: 'Test Form Beta', 
      fields: ['Department', 'Remarks', 'Date', 'Priority', 'Amount'],
      sampleData: [
        { Department: 'HR', Remarks: 'Good', Date: '2024-01-15', Priority: 'High', Amount: 5000 },
        { Department: 'IT', Remarks: 'Excellent', Date: '2024-01-16', Priority: 'Medium', Amount: 7500 },
        { Department: 'HR', Remarks: 'Average', Date: '2024-01-17', Priority: 'Low', Amount: 3000 },
        { Department: 'Finance', Remarks: 'Good', Date: '2024-01-18', Priority: 'High', Amount: 9000 },
        { Department: 'IT', Remarks: 'Good', Date: '2024-01-19', Priority: 'Medium', Amount: 6000 },
        { Department: 'Finance', Remarks: 'Excellent', Date: '2024-01-20', Priority: 'High', Amount: 8500 }
      ]
    },
    { 
      id: 3, 
      name: 'Test Form Gamma', 
      fields: ['Category', 'Amount', 'Location', 'Type', 'Count'],
      sampleData: [
        { Category: 'A', Amount: 15000, Location: 'North', Type: 'Type1', Count: 10 },
        { Category: 'B', Amount: 22000, Location: 'South', Type: 'Type2', Count: 15 },
        { Category: 'C', Amount: 18000, Location: 'East', Type: 'Type1', Count: 12 },
        { Category: 'A', Amount: 25000, Location: 'West', Type: 'Type2', Count: 20 },
        { Category: 'B', Amount: 19000, Location: 'North', Type: 'Type1', Count: 8 }
      ]
    }
  ];

  const [chartConfig, setChartConfig] = useState({
    chartName: '',
    chartType: 'bar',
    xAxisField: '',
    yAxisField: '',
    aggregation: 'count',
    filterField: '',
    filterValue: ''
  });

  const chartTypes = [
    { value: 'bar', label: 'Bar Chart', icon: BarChart3 },
    { value: 'line', label: 'Line Chart', icon: LineChart },
    { value: 'pie', label: 'Pie Chart', icon: PieChart },
    { value: 'area', label: 'Area Chart', icon: TrendingUp }
  ];

  const aggregations = [
    { value: 'count', label: 'Count' },
    { value: 'sum', label: 'Sum' },
    { value: 'average', label: 'Average' },
    { value: 'min', label: 'Minimum' },
    { value: 'max', label: 'Maximum' }
  ];

  const COLORS = ['#3d82c4', '#82ca9d', '#ffc658', '#ff8042', '#8884d8', '#a4de6c'];

  const handleFormSelect = (formId) => {
    setSelectedForm(formId);
    setDashboards([]);
  };

  const handleAddChart = () => {
    setEditingChart(null);
    setChartConfig({
      chartName: '',
      chartType: 'bar',
      xAxisField: '',
      yAxisField: '',
      aggregation: 'count',
      filterField: '',
      filterValue: ''
    });
    setShowChartModal(true);
  };

  const handleEditChart = (chart) => {
    setEditingChart(chart);
    setChartConfig(chart.config);
    setShowChartModal(true);
  };

  const handleSaveChart = () => {
    const newChart = {
      id: editingChart ? editingChart.id : Date.now(),
      formId: selectedForm,
      config: { ...chartConfig }
    };

    if (editingChart) {
      setDashboards(dashboards.map(chart => 
        chart.id === editingChart.id ? newChart : chart
      ));
    } else {
      setDashboards([...dashboards, newChart]);
    }

    setShowChartModal(false);
  };

  const handleDeleteChart = (chartId) => {
    if (window.confirm('Are you sure you want to delete this chart?')) {
      setDashboards(dashboards.filter(chart => chart.id !== chartId));
    }
  };

  const getChartIcon = (type) => {
    const chart = chartTypes.find(c => c.value === type);
    return chart ? chart.icon : BarChart3;
  };

  // Process data for chart
  const processChartData = (chart) => {
    const formData = forms.find(f => f.id === parseInt(chart.formId));
    if (!formData) return [];

    let data = [...formData.sampleData];

    // Apply filter
    if (chart.config.filterField && chart.config.filterValue) {
      data = data.filter(row => 
        String(row[chart.config.filterField]).toLowerCase().includes(
          chart.config.filterValue.toLowerCase()
        )
      );
    }

    // Group by X-axis field
    const grouped = {};
    data.forEach(row => {
      const key = row[chart.config.xAxisField];
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(row);
    });

    // Apply aggregation
    const result = Object.keys(grouped).map(key => {
      let value = 0;
      
      if (chart.config.aggregation === 'count') {
        value = grouped[key].length;
      } else if (chart.config.yAxisField) {
        const values = grouped[key]
          .map(row => parseFloat(row[chart.config.yAxisField]))
          .filter(v => !isNaN(v));
        
        switch (chart.config.aggregation) {
          case 'sum':
            value = values.reduce((a, b) => a + b, 0);
            break;
          case 'average':
            value = values.reduce((a, b) => a + b, 0) / values.length;
            break;
          case 'min':
            value = Math.min(...values);
            break;
          case 'max':
            value = Math.max(...values);
            break;
          default:
            value = values.length;
        }
      }

      return {
        name: key,
        value: Math.round(value * 100) / 100
      };
    });

    return result;
  };

  const renderChart = (chart) => {
    const data = processChartData(chart);
    const { chartType } = chart.config;

    if (data.length === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-gray-500">
          No data available
        </div>
      );
    }

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#3d82c4" name={chart.config.aggregation} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <RechartsLine data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#3d82c4" name={chart.config.aggregation} />
            </RechartsLine>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPie>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </RechartsPie>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="value" stroke="#3d82c4" fill="#3d82c4" fillOpacity={0.6} name={chart.config.aggregation} />
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  const selectedFormData = forms.find(f => f.id === parseInt(selectedForm));
  const currentDashboards = dashboards.filter(d => d.formId === selectedForm);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Custom Forms Dashboard</h1>
        <p className="text-sm text-gray-600 mt-1">Build custom dashboards for your forms</p>
      </div>

      {/* Form Selection */}
      <div className="bg-white border border-gray-200 p-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          Select Form to Build Dashboard
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {forms.map((form) => (
            <button
              key={form.id}
              onClick={() => handleFormSelect(form.id)}
              className={`p-4 border-2 transition-all text-left ${
                selectedForm === form.id.toString()
                  ? 'border-[#3d82c4] bg-blue-50'
                  : 'border-gray-200 hover:border-[#3d82c4] hover:bg-blue-50'
              }`}
            >
              <h3 className="font-semibold text-gray-800">{form.name}</h3>
              <p className="text-xs text-gray-600 mt-1">
                {form.fields.length} fields available
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Dashboard Builder */}
      {selectedForm && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">
              Dashboard for {selectedFormData?.name}
            </h2>
            <button
              onClick={handleAddChart}
              className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Chart
            </button>
          </div>

          {/* Charts Display */}
          {currentDashboards.length > 0 ? (
            <div className="space-y-6">
              {currentDashboards.map((chart) => {
                const ChartIcon = getChartIcon(chart.config.chartType);
                return (
                  <div
                    key={chart.id}
                    className="bg-white border border-gray-200 p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 text-[#3d82c4]">
                          <ChartIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {chart.config.chartName}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {chart.config.xAxisField} 
                            {chart.config.yAxisField && ` vs ${chart.config.yAxisField}`}
                            {' '}({chart.config.aggregation})
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditChart(chart)}
                          className="p-2 text-orange-600 hover:bg-orange-50 transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteChart(chart.id)}
                          className="p-2 text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Real Chart */}
                    {renderChart(chart)}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 p-12 text-center">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                No Charts Added Yet
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Start building your dashboard by adding charts
              </p>
              <button
                onClick={handleAddChart}
                className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Your First Chart
              </button>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Chart Modal */}
      {showChartModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                {editingChart ? 'Edit Chart' : 'Add New Chart'}
              </h2>
              <button
                onClick={() => setShowChartModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Chart Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Chart Name
                </label>
                <input
                  type="text"
                  value={chartConfig.chartName}
                  onChange={(e) => setChartConfig({ ...chartConfig, chartName: e.target.value })}
                  placeholder="e.g., District-wise Applications"
                  className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                />
              </div>

              {/* Chart Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Chart Type
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {chartTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setChartConfig({ ...chartConfig, chartType: type.value })}
                        className={`p-4 border-2 transition-all ${
                          chartConfig.chartType === type.value
                            ? 'border-[#3d82c4] bg-blue-50'
                            : 'border-gray-200 hover:border-[#3d82c4]'
                        }`}
                      >
                        <Icon className="w-6 h-6 mx-auto mb-2 text-[#3d82c4]" />
                        <p className="text-xs font-medium text-gray-700">{type.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* X-Axis Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  X-Axis Field (Category)
                </label>
                <select
                  value={chartConfig.xAxisField}
                  onChange={(e) => setChartConfig({ ...chartConfig, xAxisField: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                >
                  <option value="">Select Field</option>
                  {selectedFormData?.fields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </div>

              {/* Y-Axis Field (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Y-Axis Field (Value) - Optional
                </label>
                <select
                  value={chartConfig.yAxisField}
                  onChange={(e) => setChartConfig({ ...chartConfig, yAxisField: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                >
                  <option value="">None (Use Count)</option>
                  {selectedFormData?.fields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </div>

              {/* Aggregation */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Aggregation Method
                </label>
                <select
                  value={chartConfig.aggregation}
                  onChange={(e) => setChartConfig({ ...chartConfig, aggregation: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4]"
                >
                  {aggregations.map((agg) => (
                    <option key={agg.value} value={agg.value}>
                      {agg.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter (Optional) */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">
                  Filter Data (Optional)
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      Filter Field
                    </label>
                    <select
                      value={chartConfig.filterField}
                      onChange={(e) => setChartConfig({ ...chartConfig, filterField: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4] text-sm"
                    >
                      <option value="">No Filter</option>
                      {selectedFormData?.fields.map((field) => (
                        <option key={field} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">
                      Filter Value
                    </label>
                    <input
                      type="text"
                      value={chartConfig.filterValue}
                      onChange={(e) => setChartConfig({ ...chartConfig, filterValue: e.target.value })}
                      placeholder="Enter value"
                      disabled={!chartConfig.filterField}
                      className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#3d82c4] text-sm disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowChartModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChart}
                disabled={!chartConfig.chartName || !chartConfig.xAxisField}
                className="px-4 py-2 bg-[#3d82c4] text-white font-medium hover:bg-[#2d6fad] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editingChart ? 'Update Chart' : 'Add Chart'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDashboard;